import React, { useImperativeHandle } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useInput } from 'react-admin';

export interface RecaptchaInputHandle {
  reset: () => void;
}

interface RecaptchaInputProps {
  source: string;
  siteKey: string;
}

export const RecaptchaInput = React.forwardRef<RecaptchaInputHandle, RecaptchaInputProps>(
  ({ source, siteKey }, ref) => {
    const theme = useTheme();
    // noSsr: first client paint must match the viewport. Without it, MUI returns
    // false once, react-google-recaptcha renders size="normal" (304px), then
    // ignores the later size="compact" change — Card overflow:hidden clips it.
    const isXs = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const size = isXs ? 'compact' : 'normal';
    const widgetRef = React.useRef<ReCAPTCHA>(null);

    // Validation still blocks submit without a token; the message is intentionally not rendered.
    const { field } = useInput({
      source,
      validate: (value: string | null) => {
        if (!value) return 'Please complete the reCAPTCHA';
        return undefined;
      },
      defaultValue: null,
    });

    useImperativeHandle(ref, () => ({
      reset: () => {
        widgetRef.current?.reset();
        field.onChange(null);
      },
    }));

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          my: 1,
          // compact is taller (144px) than normal (78px)
          minHeight: isXs ? 144 : 78,
          overflow: 'visible',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ReCAPTCHA
            // remount if breakpoint changes — library only applies size on first render
            key={size}
            ref={widgetRef}
            sitekey={siteKey}
            size={size}
            onChange={(token) => field.onChange(token ?? null)}
            onExpired={() => field.onChange(null)}
            onErrored={() => field.onChange(null)}
          />
        </Box>
      </Box>
    );
  }
);

RecaptchaInput.displayName = 'RecaptchaInput';
